import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { PencilIcon, ArrowLeftIcon, TrashIcon, LockClosedIcon, ChatBubbleLeftIcon } from '@heroicons/react/20/solid'
import { ticketsApi, type TicketCommentInput } from '../../lib/api'
import { TicketForm } from '../../components/forms/TicketForm'
import { TicketStatusBadge } from '../../components/ui/TicketStatusBadge'
import { TicketPriorityBadge } from '../../components/ui/TicketPriorityBadge'
import { TicketCategoryBadge } from '../../components/ui/TicketCategoryBadge'
import { Avatar } from '../../components/ui/Avatar'
import { useTranslation } from '../../hooks/useTranslation'
import { useAuth } from '../../hooks/useAuth'
import type { TicketInput, TicketStatus, ticketStatuses, ticketPriorities } from '../../../shared/types'

export function TicketDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)
  const [commentBody, setCommentBody] = useState('')
  const [isInternal, setIsInternal] = useState(false)
  const { t } = useTranslation()
  const { user } = useAuth()

  const { data: ticket, isLoading } = useQuery({
    queryKey: ['tickets', id],
    queryFn: () => ticketsApi.get(id!),
    enabled: !!id,
  })

  const updateMutation = useMutation({
    mutationFn: (data: Partial<TicketInput>) => ticketsApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets', id] })
      queryClient.invalidateQueries({ queryKey: ['tickets'] })
      setIsEditing(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => ticketsApi.delete(id!),
    onSuccess: () => {
      navigate('/tickets')
    },
  })

  const addCommentMutation = useMutation({
    mutationFn: (data: TicketCommentInput) => ticketsApi.addComment(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets', id] })
      setCommentBody('')
      setIsInternal(false)
    },
  })

  const handleStatusChange = (status: TicketStatus) => {
    updateMutation.mutate({ status })
  }

  const handleCommentSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!commentBody.trim()) return
    addCommentMutation.mutate({ body: commentBody, isInternal })
  }

  if (isLoading) return <div>{t('common.loading')}</div>
  if (!ticket) return <div>{t('pages.tickets.notFound' as any)}</div>

  return (
    <>
      {/* Secondary column (desktop sidebar) */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="mb-6">
          <Link
            to="/tickets"
            className="inline-flex items-center gap-x-2 text-sm font-semibold hover:opacity-75 text-brand-500"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            {t('pages.tickets.backToList' as any)}
          </Link>
        </div>

        <div className="mb-6">
          <div className="flex items-center gap-x-2 mb-2">
            <span className="text-lg font-bold text-gray-900">{ticket.ticketNumber}</span>
            <TicketCategoryBadge category={ticket.category} />
          </div>
          <h1 className="text-xl font-semibold text-gray-900">{ticket.subject}</h1>
        </div>

        <div className="space-y-3 mb-6">
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="w-full inline-flex justify-center items-center gap-x-2 rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
          >
            <PencilIcon className="h-4 w-4" />
            {isEditing ? t('common.cancel') : t('common.edit')}
          </button>
          <button
            onClick={() => {
              if (confirm(t('pages.tickets.confirmDelete' as any))) {
                deleteMutation.mutate()
              }
            }}
            className="w-full inline-flex justify-center items-center gap-x-2 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
          >
            <TrashIcon className="h-4 w-4" />
            {t('common.delete')}
          </button>
        </div>

        <div className="border-t border-gray-200 pt-6">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">{t('common.quickInfo')}</h2>
          <dl className="space-y-4">
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.status')}</dt>
              <dd className="mt-1">
                <select
                  value={ticket.status}
                  onChange={(e) => handleStatusChange(e.target.value as TicketStatus)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                >
                  {(['open', 'in_progress', 'pending', 'resolved', 'closed'] as const).map((status) => (
                    <option key={status} value={status}>
                      {t(`pages.tickets.statuses.${status}` as any)}
                    </option>
                  ))}
                </select>
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.priority')}</dt>
              <dd className="mt-1">
                <TicketPriorityBadge priority={ticket.priority} />
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.assignedTo')}</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {ticket.assignedTo ? (
                  <div className="flex items-center gap-x-2">
                    <Avatar firstName={ticket.assignedTo.firstName ?? ''} lastName={ticket.assignedTo.lastName ?? ''} size="xs" />
                    <span>{ticket.assignedTo.firstName} {ticket.assignedTo.lastName}</span>
                  </div>
                ) : (
                  <span className="text-gray-400">{t('pages.tickets.unassigned' as any)}</span>
                )}
              </dd>
            </div>
            {ticket.dueDate && (
              <div>
                <dt className="text-xs font-medium text-gray-500">{t('fields.dueDate')}</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(ticket.dueDate).toLocaleDateString()}
                </dd>
              </div>
            )}
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.createdAt')}</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {new Date(ticket.createdAt).toLocaleDateString()}
              </dd>
            </div>
            {ticket.createdBy && (
              <div>
                <dt className="text-xs font-medium text-gray-500">{t('fields.createdBy' as any)}</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <div className="flex items-center gap-x-2">
                    <Avatar firstName={ticket.createdBy.firstName ?? ''} lastName={ticket.createdBy.lastName ?? ''} size="xs" />
                    <span>{ticket.createdBy.firstName} {ticket.createdBy.lastName}</span>
                  </div>
                </dd>
              </div>
            )}
          </dl>
        </div>

        {/* Contact/Account info for support tickets */}
        {ticket.category === 'support' && (ticket.contact || ticket.account) && (
          <div className="border-t border-gray-200 pt-6 mt-6">
            <h2 className="text-sm font-semibold text-gray-900 mb-4">{t('pages.tickets.relatedInfo' as any)}</h2>
            <dl className="space-y-4">
              {ticket.contact && (
                <div>
                  <dt className="text-xs font-medium text-gray-500">{t('fields.contact')}</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    <Link to={`/contacts/${ticket.contactId}`} className="text-brand-600 hover:text-brand-800">
                      {ticket.contact.firstName} {ticket.contact.lastName}
                    </Link>
                  </dd>
                </div>
              )}
              {ticket.account && (
                <div>
                  <dt className="text-xs font-medium text-gray-500">{t('fields.account')}</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    <Link to={`/accounts/${ticket.accountId}`} className="text-brand-600 hover:text-brand-800">
                      {ticket.account.name}
                    </Link>
                  </dd>
                </div>
              )}
            </dl>
          </div>
        )}
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] p-6">
          {/* Mobile header */}
          <div className="xl:hidden mb-6">
            <div className="mb-4">
              <Link
                to="/tickets"
                className="inline-flex items-center gap-x-2 text-sm font-semibold hover:opacity-75 text-brand-500"
              >
                <ArrowLeftIcon className="h-5 w-5" />
                {t('pages.tickets.backToList' as any)}
              </Link>
            </div>
            <div className="flex items-center gap-x-2 mb-2">
              <span className="font-bold text-gray-900">{ticket.ticketNumber}</span>
              <TicketCategoryBadge category={ticket.category} />
              <TicketStatusBadge status={ticket.status} />
              <TicketPriorityBadge priority={ticket.priority} />
            </div>
            <h1 className="text-xl font-semibold text-gray-900">{ticket.subject}</h1>
          </div>

          {isEditing ? (
            <div>
              <h2 className="text-lg font-medium mb-4">{t('common.edit')} {t('pages.tickets.title')}</h2>
              <TicketForm
                defaultValues={{
                  subject: ticket.subject,
                  description: ticket.description,
                  status: ticket.status,
                  priority: ticket.priority,
                  category: ticket.category,
                  type: ticket.type,
                  channel: ticket.channel,
                  contactId: ticket.contactId,
                  accountId: ticket.accountId,
                  assignedToId: ticket.assignedToId,
                  dueDate: ticket.dueDate ? new Date(ticket.dueDate).toISOString().slice(0, 16) : undefined,
                }}
                onSubmit={(data) => updateMutation.mutate(data)}
                onCancel={() => setIsEditing(false)}
                loading={updateMutation.isPending}
                fixedCategory={ticket.category}
              />
            </div>
          ) : (
            <>
              {/* Description */}
              <div className="mb-8">
                <h2 className="text-sm font-semibold text-gray-900 mb-2">{t('fields.description')}</h2>
                <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-lg p-4">
                  {ticket.description}
                </div>
              </div>

              {/* Comments */}
              <div>
                <h2 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <ChatBubbleLeftIcon className="h-5 w-5" />
                  {t('pages.tickets.comments' as any)} ({ticket.comments?.length || 0})
                </h2>

                {/* Comments list */}
                <div className="space-y-4 mb-6">
                  {ticket.comments?.map((comment) => (
                    <div
                      key={comment.id}
                      className={`rounded-lg p-4 ${
                        comment.isInternal
                          ? 'bg-amber-50 border border-amber-200'
                          : 'bg-gray-50 border border-gray-200'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <Avatar
                          firstName={comment.author.firstName ?? ''}
                          lastName={comment.author.lastName ?? ''}
                          size="sm"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-gray-900">
                              {comment.author.firstName} {comment.author.lastName}
                            </span>
                            {comment.isInternal && (
                              <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                                <LockClosedIcon className="h-3 w-3" />
                                {t('pages.tickets.internalNote' as any)}
                              </span>
                            )}
                            <span className="text-xs text-gray-500">
                              {new Date(comment.createdAt).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{comment.body}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {(!ticket.comments || ticket.comments.length === 0) && (
                    <p className="text-sm text-gray-500 text-center py-4">
                      {t('pages.tickets.noComments' as any)}
                    </p>
                  )}
                </div>

                {/* Add comment form */}
                <form onSubmit={handleCommentSubmit} className="border-t border-gray-200 pt-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-2">
                    {t('pages.tickets.addComment' as any)}
                  </h3>
                  <textarea
                    value={commentBody}
                    onChange={(e) => setCommentBody(e.target.value)}
                    rows={3}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                    placeholder={t('pages.tickets.commentPlaceholder' as any)}
                  />
                  <div className="mt-3 flex items-center justify-between">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={isInternal}
                        onChange={(e) => setIsInternal(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                      />
                      <span className="text-sm text-gray-700 flex items-center gap-1">
                        <LockClosedIcon className="h-4 w-4" />
                        {t('pages.tickets.markAsInternal' as any)}
                      </span>
                    </label>
                    <button
                      type="submit"
                      disabled={!commentBody.trim() || addCommentMutation.isPending}
                      className="rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-500 disabled:opacity-50"
                    >
                      {addCommentMutation.isPending ? t('common.saving') : t('pages.tickets.postComment' as any)}
                    </button>
                  </div>
                </form>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}
